import React, { useState } from 'react';

const RolePermissions = () => {
  // Sample data
  const [roles, setRoles] = useState([
    { id: 1, name: 'Admin', description: 'Full system access', userCount: 3 },
    { id: 2, name: 'HR Manager', description: 'HR management access', userCount: 5 },
    { id: 3, name: 'Department Manager', description: 'Team management access', userCount: 12 },
    { id: 4, name: 'Employee', description: 'Basic employee access', userCount: 150 }
  ]);

  const [permissions, setPermissions] = useState([
    { id: 1, module: 'Dashboard', view: true, create: true, edit: true, delete: true, role: 'Admin' },
    { id: 2, module: 'Employee Records', view: true, create: true, edit: true, delete: false, role: 'HR Manager' },
    { id: 3, module: 'Payroll', view: true, create: false, edit: false, delete: false, role: 'Employee' },
    { id: 4, module: 'Performance Reviews', view: true, create: true, edit: true, delete: false, role: 'Department Manager' }
  ]);

  const [employees, setEmployees] = useState([
    { id: 1, name: 'John Doe', email: 'john@company.com', currentRole: 'Employee', department: 'Engineering' },
    { id: 2, name: 'Jane Smith', email: 'jane@company.com', currentRole: 'HR Manager', department: 'Human Resources' },
    { id: 3, name: 'Robert Johnson', email: 'robert@company.com', currentRole: 'Department Manager', department: 'Sales' },
    { id: 4, name: 'Sarah Williams', email: 'sarah@company.com', currentRole: 'Admin', department: 'IT' }
  ]);

  const modules = ['Dashboard', 'Employee Records', 'Payroll', 'Performance Reviews', 'Recruitment', 'Time Off', 'Reports', 'Settings'];
  const permissionTypes = ['view', 'create', 'edit', 'delete'];

  // State for forms
  const [showAddRole, setShowAddRole] = useState(false);
  const [showPermissionSettings, setShowPermissionSettings] = useState(false);
  const [showAssignRole, setShowAssignRole] = useState(false);
  const [selectedRole, setSelectedRole] = useState('Admin');
  const [newRole, setNewRole] = useState({ name: '', description: '' });
  const [assignedPermissions, setAssignedPermissions] = useState({});
  const [employeeAssignment, setEmployeeAssignment] = useState({ employeeId: '', role: '' });

  // Add a new role
  const handleAddRole = () => {
    if (newRole.name.trim()) {
      const role = {
        id: roles.length + 1,
        name: newRole.name,
        description: newRole.description,
        userCount: 0
      };
      setRoles([...roles, role]);
      setNewRole({ name: '', description: '' });
      setShowAddRole(false);
    }
  };

  // Handle permission change
  const handlePermissionChange = (module, permission, value) => {
    setAssignedPermissions(prev => ({
      ...prev,
      [module]: {
        ...prev[module],
        [permission]: value
      }
    }));
  };

  // Save permission settings
  const savePermissionSettings = () => {
    // In a real app, you would save these to your backend
    alert('Permission settings saved successfully!');
    setShowPermissionSettings(false);
  };

  // Assign role to employee
  const assignRoleToEmployee = () => {
    if (employeeAssignment.employeeId && employeeAssignment.role) {
      // In a real app, you would update the employee's role in the backend
      const updatedEmployees = employees.map(emp => 
        emp.id === parseInt(employeeAssignment.employeeId) 
          ? { ...emp, currentRole: employeeAssignment.role } 
          : emp
      );
      setEmployees(updatedEmployees);
      alert('Role assigned successfully!');
      setEmployeeAssignment({ employeeId: '', role: '' });
      setShowAssignRole(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-6">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-8 text-center">
          <h1 className="text-4xl font-bold text-indigo-800 mb-2 flex items-center justify-center">
            <span className="mr-3">4️⃣</span> Role Permissions ⚙️
          </h1>
          <p className="text-gray-600">Control access and permissions for different user roles in your organization</p>
        </div>

        {/* Action Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          {/* Add Role Card */}
          <div 
            className="bg-white p-6 rounded-xl shadow-lg cursor-pointer hover:shadow-xl transition-shadow duration-300"
            onClick={() => setShowAddRole(true)}
          >
            <div className="text-4xl mb-4">➕</div>
            <h3 className="text-xl font-semibold text-gray-800 mb-2">Add Role</h3>
            <p className="text-gray-600">Create new user roles with specific access permissions</p>
          </div>

          {/* Permission Settings Card */}
          <div 
            className="bg-white p-6 rounded-xl shadow-lg cursor-pointer hover:shadow-xl transition-shadow duration-300"
            onClick={() => setShowPermissionSettings(true)}
          >
            <div className="text-4xl mb-4">🔑</div>
            <h3 className="text-xl font-semibold text-gray-800 mb-2">Permission Settings</h3>
            <p className="text-gray-600">Configure access permissions for each module by role</p>
          </div>

          {/* Assign Role Card */}
          <div 
            className="bg-white p-6 rounded-xl shadow-lg cursor-pointer hover:shadow-xl transition-shadow duration-300"
            onClick={() => setShowAssignRole(true)}
          >
            <div className="text-4xl mb-4">👤</div>
            <h3 className="text-xl font-semibold text-gray-800 mb-2">Assign Role to Employee</h3>
            <p className="text-gray-600">Assign specific roles to employees in your organization</p>
          </div>
        </div>

        {/* Add Role Form */}
        {showAddRole && (
          <div className="bg-white p-6 rounded-xl shadow-lg mb-8">
            <h2 className="text-2xl font-semibold text-gray-800 mb-4">➕ Add New Role</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-gray-700 mb-2">Role Name</label>
                <input
                  type="text"
                  value={newRole.name}
                  onChange={(e) => setNewRole({...newRole, name: e.target.value})}
                  placeholder="e.g., Content Manager"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>
              <div>
                <label className="block text-gray-700 mb-2">Description</label>
                <input
                  type="text"
                  value={newRole.description}
                  onChange={(e) => setNewRole({...newRole, description: e.target.value})}
                  placeholder="Describe this role's responsibilities"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>
            </div>
            <div className="flex gap-2">
              <button 
                onClick={handleAddRole}
                className="bg-green-500 hover:bg-green-600 text-white font-semibold py-2 px-6 rounded-lg transition-colors"
              >
                Create Role
              </button>
              <button 
                onClick={() => setShowAddRole(false)}
                className="bg-gray-500 hover:bg-gray-600 text-white font-semibold py-2 px-6 rounded-lg transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {/* Permission Settings Form */}
        {showPermissionSettings && (
          <div className="bg-white p-6 rounded-xl shadow-lg mb-8">
            <h2 className="text-2xl font-semibold text-gray-800 mb-4">🔑 Permission Settings</h2>
            
            <div className="mb-6">
              <label className="block text-gray-700 mb-2">Select Role</label>
              <select
                value={selectedRole}
                onChange={(e) => setSelectedRole(e.target.value)}
                className="w-full md:w-1/3 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
              >
                {roles.map(role => (
                  <option key={role.id} value={role.name}>{role.name}</option>
                ))}
              </select>
            </div>
            
            <div className="overflow-x-auto">
              <table className="min-w-full bg-white">
                <thead>
                  <tr className="bg-gray-100 text-gray-700">
                    <th className="py-3 px-4 text-left">Module</th>
                    {permissionTypes.map(perm => (
                      <th key={perm} className="py-3 px-4 text-center capitalize">{perm}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {modules.map(module => (
                    <tr key={module} className="border-b border-gray-200 hover:bg-gray-50">
                      <td className="py-3 px-4 font-medium">{module}</td>
                      {permissionTypes.map(perm => (
                        <td key={perm} className="py-3 px-4 text-center">
                          <input
                            type="checkbox"
                            checked={assignedPermissions[module]?.[perm] || false}
                            onChange={(e) => handlePermissionChange(module, perm, e.target.checked)}
                            className="h-5 w-5 text-indigo-600 focus:ring-indigo-500"
                          />
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            
            <div className="flex gap-2 mt-6">
              <button 
                onClick={savePermissionSettings}
                className="bg-blue-500 hover:bg-blue-600 text-white font-semibold py-2 px-6 rounded-lg transition-colors"
              >
                Save Permissions
              </button>
              <button 
                onClick={() => setShowPermissionSettings(false)}
                className="bg-gray-500 hover:bg-gray-600 text-white font-semibold py-2 px-6 rounded-lg transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {/* Assign Role Form */}
        {showAssignRole && (
          <div className="bg-white p-6 rounded-xl shadow-lg mb-8">
            <h2 className="text-2xl font-semibold text-gray-800 mb-4">👤 Assign Role to Employee</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-gray-700 mb-2">Select Employee</label>
                <select
                  value={employeeAssignment.employeeId}
                  onChange={(e) => setEmployeeAssignment({...employeeAssignment, employeeId: e.target.value})}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                >
                  <option value="">Select Employee</option>
                  {employees.map(emp => (
                    <option key={emp.id} value={emp.id}>{emp.name} ({emp.department})</option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-gray-700 mb-2">Assign Role</label>
                <select
                  value={employeeAssignment.role}
                  onChange={(e) => setEmployeeAssignment({...employeeAssignment, role: e.target.value})}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                >
                  <option value="">Select Role</option>
                  {roles.map(role => (
                    <option key={role.id} value={role.name}>{role.name}</option>
                  ))}
                </select>
              </div>
            </div>
            
            <div className="flex gap-2">
              <button 
                onClick={assignRoleToEmployee}
                className="bg-indigo-500 hover:bg-indigo-600 text-white font-semibold py-2 px-6 rounded-lg transition-colors"
              >
                Assign Role
              </button>
              <button 
                onClick={() => setShowAssignRole(false)}
                className="bg-gray-500 hover:bg-gray-600 text-white font-semibold py-2 px-6 rounded-lg transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {/* Current Roles Section */}
        <div className="bg-white rounded-xl shadow-lg overflow-hidden mb-8">
          <div className="bg-gradient-to-r from-indigo-600 to-purple-600 p-4 text-white">
            <h2 className="text-2xl font-semibold flex items-center">
              <span className="mr-2">👥</span> Current Roles
            </h2>
            <p className="text-indigo-100">Manage and view all roles in your organization</p>
          </div>
          
          <div className="p-4">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {roles.map(role => (
                <div key={role.id} className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                  <h3 className="text-lg font-semibold text-gray-800 mb-1">{role.name}</h3>
                  <p className="text-gray-600 text-sm mb-2">{role.description}</p>
                  <div className="flex justify-between items-center">
                    <span className="text-indigo-600 font-medium">{role.userCount} users</span>
                    <button className="text-blue-600 hover:text-blue-800 text-sm font-medium">
                      Edit
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Current Permissions Table */}
        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          <div className="bg-gradient-to-r from-purple-600 to-pink-600 p-4 text-white">
            <h2 className="text-2xl font-semibold flex items-center">
              <span className="mr-2">🔐</span> Current Permissions
            </h2>
            <p className="text-purple-100">View permissions assigned to different roles</p>
          </div>
          
          <div className="p-4 overflow-x-auto">
            <table className="min-w-full bg-white">
              <thead>
                <tr className="bg-gray-100 text-gray-700">
                  <th className="py-3 px-4 text-left">Module</th>
                  <th className="py-3 px-4 text-left">Role</th>
                  <th className="py-3 px-4 text-center">View</th>
                  <th className="py-3 px-4 text-center">Create</th>
                  <th className="py-3 px-4 text-center">Edit</th>
                  <th className="py-3 px-4 text-center">Delete</th>
                </tr>
              </thead>
              <tbody>
                {permissions.map(perm => (
                  <tr key={perm.id} className="border-b border-gray-200 hover:bg-gray-50">
                    <td className="py-3 px-4 font-medium">{perm.module}</td>
                    <td className="py-3 px-4">
                      <span className="bg-indigo-100 text-indigo-800 text-xs font-medium px-2.5 py-0.5 rounded">
                        {perm.role}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-center">
                      {perm.view ? '✅' : '❌'}
                    </td>
                    <td className="py-3 px-4 text-center">
                      {perm.create ? '✅' : '❌'}
                    </td>
                    <td className="py-3 px-4 text-center">
                      {perm.edit ? '✅' : '❌'}
                    </td>
                    <td className="py-3 px-4 text-center">
                      {perm.delete ? '✅' : '❌'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Info Section */}
        <div className="mt-8 bg-indigo-50 p-6 rounded-xl shadow-lg">
          <h3 className="text-xl font-semibold text-indigo-800 mb-4">ℹ️ About Role Permissions</h3>
          <p className="text-gray-700 mb-2">
            Role-based access control ensures that users only have access to the information and functions 
            they need to perform their jobs. This enhances security and simplifies permission management.
          </p>
          <ul className="list-disc list-inside text-gray-700 pl-4">
            <li>Admins have full system access</li>
            <li>HR Managers can manage employee records but not financial data</li>
            <li>Department Managers can manage their team's performance reviews</li>
            <li>Employees have limited access to their own information</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default RolePermissions;